import { Products } from '@/shared/types/Billing';

import { BASE_URL } from '../constants';
import { get, post } from '../requestMethods';
import {
  IApplyPromoRequest,
  IApplyPromoResponse,
  IBoostersInvoicePreviewRequest,
  IBoostersInvoicePreviewResponse,
  ICreateSubscriptionIntentRequest,
  ICreateSubscriptionIntentResponse,
  IGetCheckoutSessionResponse,
  IGetLatestPaymentInfoResponse,
  IGetPaymentSessionRequestData,
  IGetSubscriptionSessionRequestData,
  IPlanPreviewRequest,
  IPlanPreviewResponse,
  IPlanUpdateRequest,
  IPlanUpdateResponse,
  IPortalUpgradeRequest,
  IPurchaseBoostersRequest,
  IPurchaseBoostersResponse,
} from './types';

const URL = `${BASE_URL}/api/payments`;

export const BILLING_API = {
  applyPromoCode: (data: IApplyPromoRequest) => post<IApplyPromoResponse>(`${URL}/promotion_code`, { data }),

  purchaseBoosters: (data: IPurchaseBoostersRequest) =>
    post<IPurchaseBoostersResponse>(`${URL}/boosters/purchase`, { data }),

  // Resume Subscription (unpause via backend instead of Churnkey)
  resumeSubscription: () => post<{ ok: boolean }>(`${URL}/resume`),

      // Upgrade subscription (GET with query params)
  upgradePortal: ({ new_price_id, return_url, success_url }: IPortalUpgradeRequest) =>
    get<IGetCheckoutSessionResponse>(
      `${URL}/portal/upgrade?new_price_id=${new_price_id}&return_url=${encodeURIComponent(
        return_url,
      )}&success_url=${encodeURIComponent(success_url)}`,
    ),

  // Cancel Scheduled Downgrade
  cancelScheduledDowngrade: () => post<{ ok: boolean }>(`${URL}/cancel_scheduled_downgrade`),

  // Cancel Subscription (custom cancel via backend instead of Churnkey/Stripe)
  cancelSubscription: () => post<{ ok: boolean }>(`${URL}/cancel`),

  // Cancel Subscription Cancellation (Pending Cancellation state)
  cancelSubscriptionCancellation: () => post<{ ok: boolean }>(`${URL}/cancel_subscription_cancellation`),

  createSubscriptionIntent: (data: ICreateSubscriptionIntentRequest) =>
    post<ICreateSubscriptionIntentResponse>(`${URL}/subscription`, { data }),

  getBoostersInvoicePreview: (data: IBoostersInvoicePreviewRequest) =>
    post<IBoostersInvoicePreviewResponse>(`${URL}/boosters/preview`, { data }),

  getBuyCreditsCheckoutUrl: ({ id, quantity, success_url, cancel_url }: IGetPaymentSessionRequestData) =>
    get<IGetCheckoutSessionResponse>(
      `${URL}/create_checkout_session_payment?price_id=${id}&quantity=${quantity}&success_url=${success_url}&cancel_url=${cancel_url}`,
    ),

  // Buy subscription or minutes
  getBuySubscriptionCheckoutUrl: ({ id, success_url, cancel_url }: IGetSubscriptionSessionRequestData) =>
    get<IGetCheckoutSessionResponse>(
      `${URL}/create_checkout_session_subscription?price_id=${id}&success_url=${success_url}&cancel_url=${cancel_url}`,
    ),

  // Get link to Customer Portal
  getCustomerPortal: (url: string) => get<{ billing_portal_url: string }>(`${URL}/billing_portal?return_url=${url}`),

  // Other
  getLatestPaymentInfo: () => get<IGetLatestPaymentInfoResponse>(`${URL}/latest_payment_info`),

  getPlanInvoicePreview: ({ price_id, promotion_code }: IPlanPreviewRequest) => {
    const params = new URLSearchParams({ price_id });
    if (promotion_code) params.set('promotion_code', promotion_code);
    return post<IPlanPreviewResponse>(`${URL}/plan/preview?${params.toString()}`);
  },

  // Get all plans
  getPlans: () => get<Products>(`${URL}/products`),

  // Update customer billing details
  updateCustomerBillingDetails: (data: { [key: string]: object | string }) =>
    post<{
      ok: boolean;
    }>(`${URL}/update_customer_billing_details`, { data }),

  updatePlan: ({ price_id, promotion_code }: IPlanUpdateRequest) => {
    const params = new URLSearchParams({ price_id });
    if (promotion_code) params.set('promotion_code', promotion_code);
    return post<IPlanUpdateResponse>(`${URL}/plan/update?${params.toString()}`);
  },
};
